import type {
    IBaseColumn,
    IFilters,
    IRawData } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/types";
import {
    EIndustriesType
} from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/types";
import type { CategoryId, ICategory, IFlattenedCategory } from "app/common/services/categoryService.types";
import { ADULT_ID } from "app/common/services/categoryService";
import { columns } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/columns/columns";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PLAYERS_STATE } from "app/pages/industry-analysis/overview/pre-defined-industries-benchmarking/table/table-top/const";
import type { CategoriesMap } from "app/services/CategoryServiceUtils";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { EChartType } from "app/pages/industry-analysis/players/market-shares/types";

export interface IBaseRecord {
    totalVisits: number;
    totalVisitsChange: number;
    uniqueVisitors: number;
    uniqueVisitorsChange: number;
    industry: string;
    previousPeriodTotalVisits: number;
    previousPeriodUniqueVisitors: number;
}

interface IRecord extends IBaseRecord {
    category: IFlattenedCategory;
    id: CategoryId;
    isFavorite: boolean;
    industry: string;
    categoryHref: string;
}

type IRecords = Array<IRecord>;

export interface ITableData {
    Records: IRecords;
    TotalCount: number;
}

/**
 * returns true [i.e display the industry] if one of the followings are true:
 * 1. The selected industry is a child of the industry or the other way around.
 * 2. The selected industry and the industry have the same parent industry.
 * 3. The selected industry has no children.
 * 4. The Selected industry and the industry are the same.
 * */
const resolveIndustryResult = (selectedIndustry: IFlattenedCategory, industry: IFlattenedCategory): boolean => {
    const { isChild, parentItem, children = [], id } = selectedIndustry;
    const hasChildren = children.length > 0;
    if (industry.id === id) {return true;}
    if (isChild) {
        return industry.isChild ? industry.parentItem.id === parentItem.id : industry.id === parentItem.id;
    } else {
        return !hasChildren || industry.parentItem?.id === id;
    }
};

const recordsFilter =
    (filters: IFilters, categoriesMap: CategoriesMap) =>
    (record: IRecord): boolean => {
        const { search, isFavorites, selectedIndustry, industriesType } = filters;
        const { category, isFavorite } = record;
        const { text } = category;
        let result = true;
        if (search) {
            result = text.toLowerCase().includes(search.toLowerCase());
        }
        if (isFavorites) {
            result = result && isFavorite;
        }
        if (industriesType === EIndustriesType.MAIN) {
            result = result && !category.isChild;
        }
        const flattenSelectedIndustry = categoriesMap[selectedIndustry.forApi];
        const flattenIndustry = categoriesMap[category.forApi];
        result = result && resolveIndustryResult(flattenSelectedIndustry, flattenIndustry);
        return result;
    };

const recordsSorter =
    (filters: IFilters) =>
    (record: IRecord, otherRecord: IRecord): number => {
        const { sort } = filters;
        const { field, sortDirection } = sort;
        let results;
        if (field === columns.industry.field) {
            const { category } = record;
            const { category: otherCategory } = otherRecord;
            results = record[field].localeCompare(otherRecord[field]);
            if (sortDirection === "desc" && category.parentItem?.id === otherCategory.id) {
                results = results * -1;
            }
        } else {
            results = record[field] > otherRecord[field] ? 1 : -1;
        }
        return sortDirection === "asc" ? results : -1 * results;
    };

const defaultRecordsSorter = (record: IRecord, otherRecord: IRecord): number => {
    const { industry, category } = record;
    const { industry: otherIndustry, category: otherCategory } = otherRecord;
    const compareResult = industry.localeCompare(otherIndustry);
    if (otherCategory.id === ADULT_ID) {
        return -1;
    }
    if (category.id === ADULT_ID) {
        return 1;
    }
    return compareResult;
};

const resolveCategoryHref = (category: ICategory, params): string => {
    const navigationParams = {
        ...params,
        category: category.forUrl,
        marketSharesChartType: EChartType[EChartType.OVERTIME],
    };
    const categoryHref = proNavigator.href(PLAYERS_STATE, navigationParams);
    return categoryHref;
};

const industryRecord = (
    dataColumn: IBaseColumn,
    favorites: Array<CategoryId>,
    category: IFlattenedCategory,
    params,
): IRecord => {
    const {
        AvgVisits: totalVisits,
        AvgUnique: uniqueVisitors,
        PrevAvgVisits: previousPeriodTotalVisits,
        PrevAvgUnique: previousPeriodUniqueVisitors,
        UniqueChange: uniqueVisitorsChange,
        VisitsChange: totalVisitsChange,
    } = dataColumn;
    const { id } = category;
    const record = {
        totalVisits,
        category,
        isFavorite: favorites.includes(id),
        id,
        industry: id,
        uniqueVisitors,
        totalVisitsChange,
        uniqueVisitorsChange,
        previousPeriodTotalVisits,
        previousPeriodUniqueVisitors,
        categoryHref: resolveCategoryHref(category, params),
    };
    return record;
};

export const getTableData = (
    rawData: IRawData,
    filters: IFilters,
    favorites: Array<CategoryId>,
    params,
): ITableData => {
    const { Data: data } = rawData;
    const records: IRecords = [];
    const categoriesMap = CategoryServiceUtils.getCategoriesMap();
    data.map((dataItem) => {
        const { Children: children, Category: rawCategory } = dataItem;
        const category = categoriesMap[rawCategory];
        const record = industryRecord(dataItem, favorites, category, params);
        records.push(record);
        children.map((child) => {
            const { Category: rawCategory } = child;
            const childCategory = categoriesMap[rawCategory];
            const record = industryRecord(child, favorites, childCategory, params);
            records.push({ ...record });
        });
    });
    const { sort } = filters;
    const { sortDirection, field } = sort;
    const shouldSort = sortDirection && field;
    const filteredRecords = records.filter(recordsFilter(filters, categoriesMap));
    const sortedRecords = filteredRecords.sort(shouldSort ? recordsSorter(filters) : defaultRecordsSorter);
    const totalCount = sortedRecords.length;
    const tableData = {
        Records: sortedRecords,
        TotalCount: totalCount,
    };
    return tableData;
};
